// Improved Header Component with Accessibility Enhancements
import { useRouter } from 'next/router';
import cn from 'classnames';
import SiteLink from 'components/shared/sitelink';
import Logo from 'components/shared/logo';
import useCart from 'hooks/shop/useCart';
import { addLocaleToHref } from 'lib/utils/helpers';
import { useUI, useCustomer, useLocalWishlist } from 'hooks';
import { useGlobalModules } from 'contexts';
import Icon from 'components/shared/icon';
import { useRef } from 'react';
import styles from './header.module.scss';
import BelgiumLangSelector from '../../shared/belgium-language-selector';
import NotificationBar from '../notificationBar';

const MenuItem = ({ item, locale, router, uiContext }) => {
  const { title, link, mainLinks, submenus, id } = item;
  const { openMenuDrawer, closeMenuDrawer, displayMenuDrawer, menuDrawerContent } = uiContext;
  const isOpen = menuDrawerContent?.id === id && displayMenuDrawer;

  const handleSubmenu = () => {
    if (isOpen) {
      closeMenuDrawer();
    } else {
      openMenuDrawer({ submenus, mainLinks, id });
    }
  };

  if (!submenus?.length) {
    return (
      <SiteLink
        locale={locale}
        href={addLocaleToHref(link.slug, locale, router)}
        external={link?.externalLink}
        reloaded={link?.pageReload}
        className={styles.menuItem}
        onClick={closeMenuDrawer}
        aria-label={title} // Add descriptive aria-label
      >
        <span>{title}</span>
      </SiteLink>
    );
  }

  return (
    <button
      type="button"
      className={styles.menuItem}
      onClick={handleSubmenu}
      aria-expanded={Boolean(isOpen)}
    >
      <span>{title}</span>
      <Icon
        type="chevron"
        className={cn(styles.arrow, { [styles.arrowOpen]: isOpen })}
        aria-hidden="true"
      />
    </button>
  );
};

const WishlistButton = ({ locale, router, uiContext }) => {
  const { closeMenuDrawer } = uiContext;
  const { localWishlist } = useLocalWishlist();
  const count = localWishlist ? localWishlist.length : 0;

  return (
    <SiteLink
      locale={locale}
      href={addLocaleToHref('/wishlist/', locale, router)}
      prefetch={false}
      className={cn(styles.iconButton, styles.wishlistButton)}
      onClick={closeMenuDrawer}
      aria-label={`Wishlist, ${count} ${count === 1 ? 'item' : 'items'}`}
    >
      <Icon type="wishlist" className={styles.icon} aria-hidden="true" />
      {count > 0 && (
        <span className={styles.itemQuantity} aria-hidden="true">
          {count}
        </span>
      )}
    </SiteLink>
  );
};

const AccountButton = ({ locale, uiContext }) => {
  const { customer } = useCustomer();
  const { closeMenuDrawer } = uiContext;
  const accountLabel = customer?.isLoggedIn ? 'My Account' : 'Sign In';

  return (
    <SiteLink
      className={styles.iconButton}
      locale={locale}
      href={addLocaleToHref('/account/dashboard/', locale)}
      onClick={closeMenuDrawer}
      aria-label={accountLabel} // Dynamic label based on login state
    >
      <Icon type="account" className={styles.icon} aria-hidden="true" />
    </SiteLink>
  );
};

const CartButton = ({ uiContext }) => {
  const { openCartPopup, closeMenuDrawer } = uiContext;

  const { cart, isEmpty } = useCart();
  const lineItems = cart?.lines?.edges || [];
  const totalLineItemsQuantity = lineItems.reduce(
    (total, item) => total + (item?.node?.quantity || 0),
    0
  );

  return (
    <button
      type="button"
      className={styles.iconButton}
      onClick={() => {
        closeMenuDrawer();
        openCartPopup();
      }}
      aria-label={`Cart, ${totalLineItemsQuantity} ${
        totalLineItemsQuantity === 1 ? 'item' : 'items'
      }`}
      aria-haspopup="dialog"
    >
      <Icon type="cart" className={styles.icon} aria-hidden="true" />
      {!isEmpty && (
        <span className={styles.itemQuantity} aria-hidden="true">
          {totalLineItemsQuantity}
        </span>
      )}
    </button>
  );
};

const MobileMenuButton = ({ uiContext }) => {
  const { openMenuDrawer, closeMenuDrawer, displayMenuDrawer } = uiContext;
  const buttonLabel = displayMenuDrawer ? 'Close menu' : 'Open menu';

  return (
    <button
      type="button"
      className={cn(styles.iconButton, { [styles.closeButton]: displayMenuDrawer })}
      onClick={() => {
        if (displayMenuDrawer) {
          closeMenuDrawer();
        } else {
          openMenuDrawer();
        }
      }}
      aria-expanded={displayMenuDrawer} // Dynamic expanded state
      aria-label={buttonLabel} // Dynamic label
      aria-controls="mobile-menu" // Links to the controlled element
    >
      {displayMenuDrawer ? (
        <Icon type="close" className={styles.icon} aria-hidden="true" />
      ) : (
        <Icon type="burger" className={styles.icon} aria-hidden="true" />
      )}
    </button>
  );
};

const SearchButton = ({ locale, uiContext, searchLabel }) => {
  const { closeMenuDrawer } = uiContext;
  return (
    <SiteLink
      className={cn({ [styles.menuItem]: !!searchLabel, [styles.iconButton]: !searchLabel })}
      locale={locale}
      href={addLocaleToHref('/search/', locale)}
      onClick={closeMenuDrawer}
      aria-label="Search" // Clear descriptive label
    >
      {searchLabel ? (
        <span>{searchLabel}</span>
      ) : (
        <Icon type="search" className={styles.icon} aria-hidden="true" />
      )}
    </SiteLink>
  );
};

const DoofinderSearchButton = ({ uiContext, doofinderSearchInputRef, searchLabel }) => {
  const { closeMenuDrawer } = uiContext;

  const handleSearch = e => {
    e.preventDefault();
    if (doofinderSearchInputRef.current) {
      closeMenuDrawer();
      doofinderSearchInputRef.current.focus();
    }
  };

  return (
    <button
      type="button"
      className={cn({ [styles.menuItem]: !!searchLabel, [styles.iconButton]: !searchLabel })}
      onClick={handleSearch}
      aria-label="Search"
    >
      {searchLabel ? (
        <span>{searchLabel}</span>
      ) : (
        <Icon type="search" className={styles.icon} aria-hidden="true" />
      )}
    </button>
  );
};

const HeaderDesktop = ({
  navigation,
  locale,
  router,
  uiContext,
  pageData,
  hasWebshop,
  doofinderEnabled,
  doofinderSearchInputRef,
  searchLabel,
}) => {
  const { leftPart, rightPart } = navigation?.desktop?.header || {};

  return (
    <header className={cn(styles.header, styles.headerDesktop)} role="banner">
      <nav className={styles.menu} aria-label="Main navigation">
        {leftPart?.map((item, index) => (
          <MenuItem
            key={`left_part_menu_${item.title || index}`} // Use title for better key when available
            item={item}
            locale={locale}
            router={router}
            uiContext={uiContext}
          />
        ))}
        {doofinderEnabled ? (
          <DoofinderSearchButton
            uiContext={uiContext}
            doofinderSearchInputRef={doofinderSearchInputRef}
            searchLabel={searchLabel}
          />
        ) : (
          <SearchButton locale={locale} uiContext={uiContext} searchLabel={searchLabel} />
        )}
      </nav>
      <div className={styles.logo}>
        <SiteLink
          href="/"
          className={styles.logoLink}
          onClick={uiContext.closeMenuDrawer}
          aria-label="Home"
        >
          <Logo locale={locale} />
        </SiteLink>
      </div>
      <nav className={styles.menu} aria-label="Secondary navigation">
        {rightPart?.map((item, index) => (
          <MenuItem
            key={`right_part_menu_${item.title || index}`} // Use title for better key when available
            item={item}
            locale={locale}
            router={router}
            uiContext={uiContext}
          />
        ))}
        {hasWebshop && (
          <span className={styles.icons} role="group" aria-label="User actions">
            <BelgiumLangSelector locale={locale} pageData={pageData} />
            <AccountButton locale={locale} uiContext={uiContext} />
            <WishlistButton locale={locale} router={router} uiContext={uiContext} />
            <CartButton locale={locale} uiContext={uiContext} />
          </span>
        )}
      </nav>
    </header>
  );
};

const HeaderMobile = ({
  locale,
  router,
  uiContext,
  pageData,
  hasWebshop,
  doofinderEnabled,
  doofinderSearchInputRef,
}) => (
  <header className={cn(styles.header, styles.headerMobile)} role="banner">
    <span className={cn(styles.icons, styles.iconsLeft)} role="group" aria-label="Menu and search">
      <MobileMenuButton uiContext={uiContext} />
      {doofinderEnabled ? (
        <DoofinderSearchButton
          uiContext={uiContext}
          doofinderSearchInputRef={doofinderSearchInputRef}
        />
      ) : (
        <SearchButton locale={locale} uiContext={uiContext} />
      )}
      <BelgiumLangSelector locale={locale} uiContext={uiContext} pageData={pageData} />
    </span>
    <div className={styles.logo}>
      <SiteLink
        href="/"
        className={styles.logoLink}
        onClick={uiContext.closeMenuDrawer}
        aria-label="Home"
      >
        <Logo locale={locale} />
      </SiteLink>
    </div>
    {hasWebshop && (
      <span className={cn(styles.icons, styles.iconsRight)} role="group" aria-label="User actions">
        <AccountButton locale={locale} uiContext={uiContext} />
        <WishlistButton locale={locale} router={router} uiContext={uiContext} />
        <CartButton locale={locale} uiContext={uiContext} />
      </span>
    )}
  </header>
);

const Header = ({ locale, pageData }) => {
  const router = useRouter();
  const uiContext = useUI();
  const globalModules = useGlobalModules();
  const { navigation } = globalModules || {};
  const { hasWebshop } = globalModules?.settings?.locale || {};
  const doofinderEnabled =
    globalModules?.settings?.doofinder?.enabled && globalModules?.settings?.doofinder?.hashID;
  const doofinderSearchInputRef = useRef(null);

  return (
    <>
      {doofinderEnabled && (
        // used to trigger the doofindetr widget
        <input
          type="text"
          className={styles.hiddenSearchInput}
          id="keuneSearchInput"
          ref={doofinderSearchInputRef}
          aria-label={globalModules?.translations?.search?.search || 'Search'}
        />
      )}
      <NotificationBar locale={locale} />
      <HeaderMobile
        navigation={navigation}
        locale={locale}
        router={router}
        uiContext={uiContext}
        pageData={pageData}
        hasWebshop={hasWebshop}
        doofinderEnabled={doofinderEnabled}
        doofinderSearchInputRef={doofinderSearchInputRef}
      />
      <HeaderDesktop
        navigation={navigation}
        locale={locale}
        router={router}
        uiContext={uiContext}
        pageData={pageData}
        hasWebshop={hasWebshop}
        doofinderEnabled={doofinderEnabled}
        doofinderSearchInputRef={doofinderSearchInputRef}
        searchLabel={globalModules?.translations.search?.search}
      />
    </>
  );
};

export default Header;
